import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import { useLatest } from 'ahooks';
import type { loader } from '~/routes/actions.search.aggregate';
import { useFeatureFlags } from '@modules/root';
import type { SearchIndex, VisibilityType } from '@modules/search/type/api';
import type { SearchServiceQueryFilters } from '@modules-rocco/search/types/api';
import { SEARCH_AGGREGATE_API } from '@modules-rocco/search/config/search-service.config';
import type { FilterStates } from '@modules-rocco/filters/types/filter-states';
import type { SearchSortOptions } from '@modules-rocco/search/types/enum/search-sort-options';
import { mapFilterStatesToQueryAggregations } from '@modules-rocco/search/data-mappers/filter-states-to-query-aggregations';
import { mapFilterStatesToURL } from '@modules-rocco/filters/data-mappers/filter-states-to-url';
import {
    type AggregationArgs,
    isFilterAggregation,
    isSearchableListAggregation,
} from '@rocco/ui/filter-sort/hooks/use-search-filters';
import { parseURL } from '@modules-rocco/url/api/parse-url';
import { rewriteProductFilterPathToQuery } from '~/modules/products-category/util/seo-filter-path';

// ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄
// General interfaces for this hook
// ▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀

type AggregateState = 'wait' | 'loading' | 'loaded' | 'cancel';

const defaultBuildDestinationURL =
    (
        defaultSort: SearchSortOptions | undefined,
        featureFlags: ReturnType<typeof useFeatureFlags>,
    ) =>
    (filterStates: FilterStates, currentURL: string) =>
        mapFilterStatesToURL(
            filterStates,
            currentURL,
            undefined,
            defaultSort,
            featureFlags,
        );

interface AggregateRequest {
    args: AggregationArgs;
    /** The URL a commit of these filter states would navigate to. */
    destinationURL?: string;
    onAggregateStateChange: (
        state: AggregateState,
        data?: FilterStates,
    ) => void;
}

// ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄
// `useAggregation` hook
// ▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀

interface UseAggregationProps {
    searchIndex?: SearchIndex;
    visibilityType: VisibilityType;
    /**
     * The surface's default sort. Passed into the destination-URL build so
     * the URL matches what a commit navigation produces (the commit path
     * omits the sort segment when it equals the default) — prefetches only
     * pay off when the URLs are byte-identical.
     */
    defaultSort?: SearchSortOptions;
    /**
     * Overrides the commit-URL builder. Surfaces whose commit navigation is
     * not the browse `mapFilterStatesToURL` (the professional tabs) supply
     * their own so the aggregate scoping and prefetch URL match what a real
     * commit would produce.
     */
    buildDestinationURL?: (
        filterStates: FilterStates,
        currentURL: string,
    ) => string;
    /**
     * ID-based scope filters that can't ride the destination URL's filter
     * states (partners' professionalParents, case-studies'
     * professionalsUsed). Sent alongside every aggregate request.
     */
    scopeFilters?: SearchServiceQueryFilters;
}

/**
 * This hook is used to fetch the Search Service aggregation result based on filter states and params
 */
export const useAggregation = ({
    searchIndex,
    visibilityType,
    defaultSort,
    buildDestinationURL,
    scopeFilters,
}: UseAggregationProps) => {
    const fetcher = useFetcher<typeof loader>();
    const aggregateFetcher = useLatest(fetcher);
    const requests = useRef<AggregateRequest[]>([]);
    const currentRequest = useRef<AggregateRequest | null>(null);
    const featureFlags = useFeatureFlags();
    // The destination URL of the last COMPLETED filter aggregation — the URL
    // a commit would navigate to. Hosts prefetch it so the route data is
    // already in the browser cache when the user applies.
    const [settledDestinationURL, setSettledDestinationURL] = useState<
        string | null
    >(null);

    const cancelRequest = useCallback((request: AggregateRequest) => {
        request.onAggregateStateChange('cancel');

        // check if it's a current request - we need to cancel fetcher
        if (currentRequest.current === request) {
            currentRequest.current = null;
        }

        // remove from all requests
        const allRequests = requests.current;
        const index = allRequests.indexOf(request);
        if (index > -1) {
            allRequests.splice(index, 1);
        }
    }, []);

    const executeAggregate = useCallback(() => {
        const allRequests = requests.current;

        if (
            aggregateFetcher.current.state !== 'idle' ||
            !allRequests.length ||
            currentRequest.current
        ) {
            return;
        }

        const request = allRequests.shift();

        if (!request) {
            executeAggregate();
            return;
        }

        currentRequest.current = request;

        const aggregationParams = isFilterAggregation(request.args)
            ? request.args.scope === 'count'
                ? // The search service always returns totalCount for the
                  // filtered query; an empty suite is the count-only request.
                  {}
                : mapFilterStatesToQueryAggregations(
                      request.args.filterStates,
                      searchIndex,
                  )
            : request.args.aggregationsParams;

        if (!aggregationParams) {
            cancelRequest(request);
            executeAggregate();
            return;
        }

        const aggVisibilityType =
            request.args.filterStates?.visibilityType ?? visibilityType;
        // if we have FilterAggregateRequest or grouping options then we need to build new URL with filters included
        // as in modal filters we don't change current URL right after adding / removing a filter value
        // for other cases it should be safe to use current URL
        // The suppliers view filters products but lists the brands behind them,
        // so it aggregates against the products index with `/suppliers` removed
        // and asks the endpoint to report the professional count instead.
        const isSuppliersView = location.pathname.includes('/suppliers');
        const currentURL =
            location.pathname.replace('/suppliers', '') + location.search;
        // Build the same URL a commit navigation would: same default sort
        // (so the sort segment drops when redundant) and without the
        // user-prefs flag (its param only triggers a server redirect).
        const destinationURL = request.args.filterStates
            ? (
                  buildDestinationURL ??
                  defaultBuildDestinationURL(defaultSort, featureFlags)
              )(
                  {
                      ...request.args.filterStates,
                      shouldUpdateUserPreferences: undefined,
                  },
                  currentURL,
              )
            : currentURL;
        if (isFilterAggregation(request.args)) {
            request.destinationURL = destinationURL;
        }
        // Single colour/material filters canonically live in the path
        // (`/material-{slug}`), which the Rocco parser reads as category
        // segments — the aggregate would then scope to a category that does
        // not exist and every facet list comes back empty. Rewrite them to
        // query form, as the products loader does.
        const aggregateURL = rewriteProductFilterPathToQuery(
            new URL(destinationURL, 'http://dummy'),
        );
        const urlInfo = parseURL(
            aggregateURL.pathname + aggregateURL.search,
            featureFlags,
        );

        // TODO: get sort?
        // TODO: do we need explicitly add categoryLink here (e.g. like the old web)? why?

        request.onAggregateStateChange('loading');
        aggregateFetcher.current.load(
            `${SEARCH_AGGREGATE_API}?index=${searchIndex}&visibilityType=${aggVisibilityType}&url=${encodeURIComponent(
                JSON.stringify(urlInfo),
            )}&aggregationQuery=${encodeURIComponent(
                JSON.stringify(aggregationParams),
            )}${isSuppliersView ? '&countBy=professionals' : ''}${
                scopeFilters
                    ? `&scopeFilters=${encodeURIComponent(JSON.stringify(scopeFilters))}`
                    : ''
            }`,
        );
    }, [
        featureFlags,
        aggregateFetcher,
        searchIndex,
        visibilityType,
        defaultSort,
        buildDestinationURL,
        scopeFilters,
        cancelRequest,
    ]);

    const handleAggregate = useCallback(
        (args: AggregationArgs) => {
            const allRequests = requests.current;

            if (isSearchableListAggregation(args)) {
                const duplicateIndex = allRequests.findIndex(
                    (queueRequest) =>
                        queueRequest.args.aggregateType ===
                            args.aggregateType &&
                        queueRequest.args.aggregateFilter ===
                            args.aggregateFilter,
                );

                if (duplicateIndex !== -1) {
                    cancelRequest(allRequests[duplicateIndex]!);
                }
            }

            // There's should be only one filter aggregation running at the time
            if (isFilterAggregation(args)) {
                // check current running filter aggregation & cancel it
                if (
                    currentRequest.current?.args.aggregateType ===
                    args.aggregateType
                ) {
                    cancelRequest(currentRequest.current);
                }

                // Find all duplicate filter aggregations with the same filter group
                const duplicateIndices = allRequests
                    .map((request, index) => ({ request, index }))
                    .filter(
                        ({ request }) =>
                            request.args.aggregateType === args.aggregateType,
                    );

                // Cancel and remove all duplicates
                if (duplicateIndices.length > 0) {
                    duplicateIndices.forEach(({ request }) => {
                        cancelRequest(request);
                    });
                }
            }

            const newRequest: AggregateRequest = {
                args,
                onAggregateStateChange: (state, data) => {
                    if (state === 'wait') {
                        args.onStart();
                    }

                    if (state === 'loaded') {
                        if (data) {
                            args.onSuccess(data);
                        }
                        args.onFinish();
                    }

                    if (state === 'cancel') {
                        args.onFinish();
                    }
                },
            };

            allRequests.push(newRequest);
            newRequest.onAggregateStateChange('wait');
            executeAggregate();
        },
        [cancelRequest, executeAggregate],
    );

    const resetAggregationQueue = useCallback(() => {
        if (currentRequest.current) {
            cancelRequest(currentRequest.current);
        }

        requests.current.forEach((request) => {
            cancelRequest(request);
        });
    }, [cancelRequest]);

    useEffect(() => {
        if (aggregateFetcher.current.state !== 'idle') {
            return;
        }

        if (!currentRequest.current) {
            // The in-flight request was cancelled mid-flight (a newer filter
            // aggregation superseded it) — this effect is the only
            // dispatcher once the fetcher idles, so kick the queue here or
            // the superseding request is stranded with its loading state
            // stuck on.
            if (requests.current.length) {
                executeAggregate();
            }
            return;
        }

        const request = currentRequest.current;
        const data = aggregateFetcher.current.data;

        request.onAggregateStateChange('loaded', data ?? undefined);
        if (request.destinationURL) {
            setSettledDestinationURL(request.destinationURL);
        }
        currentRequest.current = null;

        if (requests.current.length) {
            executeAggregate();
        }
    }, [
        aggregateFetcher,
        aggregateFetcher.current.state,
        aggregateFetcher.current.data,
        currentRequest,
        executeAggregate,
    ]);

    return {
        handleAggregate,
        resetAggregationQueue,
        loading: fetcher.state === 'loading',
        settledDestinationURL,
    };
};
